import { bulkUpdate, fetchPage } from '@/lists/api'
import type { ColumnDescriptor, ListViewDefinition } from '@/lists/types'
import { ApiError } from '@/lib/api'
import type { EntityRecord, FormValues } from './types'

/**
 * Доступ к одной записи (тикет #66, ТЗ §5.1).
 *
 * ## Почему через список, а не через свой эндпоинт
 *
 * Выделенного REST карточки (`GET/PUT /records/{entity}/{id}`) на момент этой задачи нет:
 * тикеты #67 и #68 выводят наружу **бизнес-операции** (выпустить счёт, провести отгрузку),
 * а не универсальное чтение и запись записи. Дожидаться их значило бы не сделать карточку
 * вовсе; написать свой контроллер — уехать в чужой тикет и получить два реестра колонок,
 * которые разойдутся.
 *
 * Поэтому чтение идёт `POST /lists/{entity}/page` с фильтром `id eq {id}`. Это не обход
 * правил, а тот же путь, которым нарисован список: та же область видимости (ADR-0007), те
 * же полевые права (сервер не описывает колонку, на которую нет гранта), тот же каталог
 * колонок. Карточка, читающая мимо этого, отличалась бы от списка правами — то есть
 * показывала бы строку, которой в списке нет.
 *
 * ## Что здесь спрятано ради замены
 *
 * Весь модуль — один слой: карточка вызывает {@link fetchRecord} и {@link saveRecord} и не
 * знает, куда они ходят. Появится выделенный эндпоинт — меняются тела этих двух функций,
 * а не компоненты. Именно ради этого доступ вынесен отдельно, а не размазан по хукам.
 *
 * ## Пробел, который надо назвать вслух
 *
 * `version` **не объявлен в каталоге списков** ни у одной сущности
 * (`ListCatalog.build()`), поэтому через этот путь его получить нельзя. Оптимистичная
 * блокировка реализована целиком — сравнение, конфликт, обе стороны на экране, — но её
 * входной ток сейчас пуст: см. {@link fetchRecord} и {@link RECORD_VERSION_COLUMN}. До тех
 * пор карточка честно показывает, что защиты нет, вместо того чтобы делать вид, что есть.
 * Заглушка, выглядящая рабочей, находится через полгода не тем человеком.
 */

/**
 * Имя колонки версии в каталоге списков.
 *
 * Каталог её не объявляет — колонка `version` есть в каждой таблице (`BaseEntity`), но в
 * `ListCatalog` не внесена. Константа существует, чтобы включение свелось к одной строке
 * в каталоге на сервере: как только `version` там появится, чтение начнёт её приносить без
 * единой правки здесь.
 */
export const RECORD_VERSION_COLUMN = 'version'

/**
 * Читает одну запись.
 *
 * Запрашиваются **все** колонки, которые сервер объявил доступными, а не колонки текущего
 * представления списка: карточка показывает запись целиком, а представление — это выбор
 * пользователя для таблицы. Взять оттуда значило бы, что карточка пустеет, когда в списке
 * оставили одну колонку.
 *
 * Отсутствие строки — это `null`, а не исключение: запись могли удалить, а могли не дать
 * прав на неё. Различить эти два случая клиент не может и не должен пытаться — оба
 * означают «этой записи для вас нет».
 */
export async function fetchRecord(
  entity: string,
  id: string,
  columns: ColumnDescriptor[],
  signal?: AbortSignal,
): Promise<EntityRecord | null> {
  const names = columns.map((column) => column.name)
  const definition: ListViewDefinition = {
    // Идентификатор запрашивается явно: сервер отбирает его и сам (без него не собрать
    // курсор), но в списке колонок ответа его нет, и полагаться на побочный эффект значит
    // сломаться при первой правке компилятора.
    columns: names.includes('id') ? names : ['id', ...names],
    filters: [{ column: 'id', operator: 'eq', value: id }],
    sort: [],
    pageSize: 1,
  }

  const page = await fetchPage(entity, definition, null, signal)
  const row = page.rows[0]
  if (!row) return null

  const values: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(row)) {
    if (key === RECORD_VERSION_COLUMN) continue
    values[key] = value
  }

  return { id: row.id, values, version: readVersion(row) }
}

/**
 * Достаёт версию из строки.
 *
 * Сегодня всегда `null`: каталог колонку не объявляет. Отдельной функцией, а не
 * `row.version as number`, ровно потому, что «версии нет» и «версия равна нулю» — разные
 * утверждения, и второе молча отключило бы проверку конфликта, объявив её пройденной.
 */
function readVersion(row: Record<string, unknown>): number | null {
  const raw = row[RECORD_VERSION_COLUMN]
  if (typeof raw === 'number') return raw
  // Число из PostgreSQL через JSON может приехать строкой у bigint-колонок.
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) {
    return Number(raw)
  }
  return null
}

/**
 * Ошибка сохранения, распознанная как конфликт версий.
 *
 * Отдельный класс, а не проверка кода в компоненте: код конфликта знает слой доступа, и
 * размазанная по экранам строка `'optimistic_lock'` разъедется с сервером незаметно.
 */
export class ConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ConflictError'
  }
}

/**
 * Коды и статусы, которыми сервер сообщает о параллельной правке.
 *
 * `409` — общий HTTP-смысл конфликта; коды перечислены рядом, потому что
 * `ObjectOptimisticLockingFailureException` в этом проекте отображается кодом, а не только
 * статусом (см. `docs/api-errors.md`).
 */
export function isConflict(error: unknown): boolean {
  if (error instanceof ConflictError) return true
  if (!(error instanceof ApiError)) return false
  if (error.status === 409) return true
  return error.code === 'optimistic_lock' || error.code === 'version_conflict'
}

/**
 * Сохраняет изменённые поля записи.
 *
 * ## Почему передаются только изменённые поля
 *
 * Полная замена документа затирает поля, которых пользователь не касался, — то есть
 * параллельную правку соседа, сделанную в поле, которого на этом экране даже не было.
 * Та же логика, что у значений пользовательских полей (#50): сервер сливает, а не заменяет.
 *
 * ## Чем это сохраняется сейчас
 *
 * `POST /lists/{entity}/bulk/update` — единственный существующий путь записи по колонке.
 * Он берёт одну колонку за раз, поэтому изменённые поля уходят **последовательно**, и это
 * его главный недостаток: транзакции на всю карточку нет, и падение на третьем поле
 * оставляет первые два записанными. Отсюда возврат {@link SaveOutcome} со списком
 * применённых полей — карточка обязана показать, что именно легло, а не рапортовать
 * «сохранено» или «не сохранено» целиком.
 *
 * Сервер к тому же принимает только колонки, объявленные `importable`: остальные он
 * отвергает кодом `bulk.column_not_editable`. Это его решение, а не наше — карточка
 * передаёт отказ пользователю дословно, вместо того чтобы прятать поля, которые сервер
 * однажды разрешит.
 *
 * Выделенный `PUT /records/{entity}/{id}` заменит тело этой функции целиком: одна
 * транзакция, `version` в теле, конфликт статусом 409. Сигнатура под это уже подходит —
 * `expectedVersion` здесь именно для того, чтобы не менять вызывающий код.
 */
export interface SaveOutcome {
  /** Поля, которые сервер действительно записал. */
  applied: string[]
  /** Поле, на котором остановились, и почему. `null`, если дошли до конца. */
  failure: { field: string; error: unknown } | null
}

export async function saveRecord(
  entity: string,
  id: string,
  changes: FormValues,
  expectedVersion: number | null,
): Promise<SaveOutcome> {
  const applied: string[] = []

  for (const [field, value] of Object.entries(changes)) {
    try {
      const result = await bulkUpdate(entity, [id], field, value)
      // Ноль затронутых строк при одной запрошенной — это не успех: право `update` уже
      // ушло на сервер, и строка не изменилась, потому что область видимости её не
      // пропустила либо её кто-то удалил. Рапортовать «сохранено» здесь значит соврать
      // ровно тому, кто потом не найдёт своей правки.
      if (result.affected === 0) {
        return {
          applied,
          failure: { field, error: new ConflictError('card.notAffected') },
        }
      }
      applied.push(field)
    } catch (error) {
      return { applied, failure: { field, error } }
    }
  }

  // expectedVersion сегодня не с чем сверить: сервер версию не принимает и не отдаёт.
  // Параметр удерживается в сигнатуре осознанно — он часть контракта, который выполнится
  // выделенным эндпоинтом, и добавлять его потом значит трогать все вызывающие места.
  void expectedVersion

  return { applied, failure: null }
}

/**
 * Поддерживает ли текущий путь записи оптимистичную блокировку.
 *
 * Одна точка правды на весь интерфейс: карточка спрашивает это, чтобы решить, обещать
 * защиту от параллельной правки или честно сказать, что её нет. Разбросанная по
 * компонентам проверка «а есть ли version» разойдётся при включении.
 */
export function supportsOptimisticLocking(record: EntityRecord | null): boolean {
  return record?.version !== null && record?.version !== undefined
}
